import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton, { iconButtonClasses } from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { Subscription } from '@rails/actioncable';
import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Account_Update_Bulk,
  AccountDayJobStat_Read,
  Driver_Read_Typeahead,
  getV1CompaniesAccountDayJobStatsTypeahead,
  getV1CompaniesCompanyIdDriversTypeahead,
  patchV1CompaniesCompanyIdAccountsBulkUpdate,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { debounce, isNil, noop } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import ToggleSidebarButton from '~components/Buttons/ToggleSidebarButton';
import Menu from '~components/Menu/Menu';
import {
  LoadingSpinner,
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import OrderBySelector, {
  AvailableOrderOption,
  OrderOption,
} from '~components/OrderBySelector/OrderBySelector';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import {
  DEFAULT_API_PAGE_SIZE,
  GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS,
} from '~constants/consts';
import { OrdersDispatchStorageKeys } from '~constants/dispatch';
import { JobAssignmentType } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import { SummaryCounts } from '~pages/Dispatch/components/SummaryCounts';
import connection from '~services/connectionModule';
import { extractPagination, Pagination } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX,
  ORDERS_DISPATCH_HEADER_ROW_HEIGHT_IN_PX,
} from './constants';
import DriverPanelAssigneeCard, {
  DriverPanelAssigneeCardPlacement,
  getVendorPanelItemSize,
} from './DriverPanelAssigneeCard';
import { SearchInput } from './drivers/Filters/SearchInput';
import InfiniteList from './drivers/InfiniteList';

const DRIVER_PANEL_SIDEBAR_ANIMATION_IN_MS = 300;
const DRIVER_PANEL_TOP_ROW_VERTICAL_GUTTER_SIZE = 0.5;
const DRIVER_PANEL_TOP_ROW_BORDER_BOTTOM_WIDTH_IN_PX = 1;
const DRIVER_PANEL_TOP_ROW_INNER_GUTTER_SIZE = 1.5;

const assigneeTypeTabs: TabProps<JobAssignmentType>[] = [
  {
    label: t('dispatch.job.assignment_popover.driver'),
    value: JobAssignmentType.DRIVER,
  },
  {
    label: t('dispatch.job.assignment_popover.vendor'),
    value: JobAssignmentType.VENDOR,
  },
];

export interface OrdersViewDriverPanelHandler {
  refetch: () => void;
}

interface OrdersViewDriverPanelProps {
  onEditDriver?: (driver: DriverTypeahead) => void;
  onEditVendor?: (vendor: AccountCapacityTypeahead) => void;
  summaryCountsRefreshToken?: number;
}

const OrdersViewDriverPanel = forwardRef<
  OrdersViewDriverPanelHandler,
  OrdersViewDriverPanelProps
>(function OrdersViewDriverPanel(
  { onEditDriver, onEditVendor, summaryCountsRefreshToken },
  ref,
) {
  const theme = useTheme();
  const { ordersDispatchStore, userStore } = useStores();

  const [searchQuery, setSearchQuery] = useState({ input: '', applied: '' });
  const companyId = getEffectiveUserCompanyId(userStore);

  useEffect(() => {
    setSearchQuery({ input: '', applied: '' });
  }, [companyId]);

  const isSidebarCollapsed = ordersDispatchStore.isSidebarCollapsed;
  const [sidebarAnimationFinished, setSidebarAnimationFinished] = useState(
    Boolean(isSidebarCollapsed),
  );

  const switchAssigneeTabsOrderFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.switchAssigneeTabsOrder,
  });

  const [selectedAssigneeType, setSelectedAssigneeType] = useState<JobAssignmentType>(
    switchAssigneeTabsOrderFeatureFlagEnabled
      ? JobAssignmentType.VENDOR
      : JobAssignmentType.DRIVER,
  );

  const sortedAssigneeTypeTabs = useMemo(() => {
    if (switchAssigneeTabsOrderFeatureFlagEnabled) {
      return [...assigneeTypeTabs].reverse();
    }

    return assigneeTypeTabs;
  }, [switchAssigneeTabsOrderFeatureFlagEnabled]);

  const shouldShowContent = !isSidebarCollapsed && sidebarAnimationFinished;

  const dateString = useMemo(() => {
    if (ordersDispatchStore.filters.startDate) {
      const formattedDate = dayjs
        .tz(ordersDispatchStore.filters.startDate)
        .format('MMM-DD-YYYY');

      return t('dispatch.dispatch_v2.driver_panel.date_status', { date: formattedDate });
    }

    return t('dispatch.dispatch_v2.filters.all_dates');
  }, [ordersDispatchStore.filters.startDate]);

  useEffect(() => {
    const debouncedApplySearchQuery = debounce(() => {
      setSearchQuery((state) => ({ ...state, applied: searchQuery.input }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

    debouncedApplySearchQuery();

    return () => {
      debouncedApplySearchQuery.cancel();
    };
  }, [searchQuery.input]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (isNil(isSidebarCollapsed)) {
      return;
    }

    if (isSidebarCollapsed) {
      setSidebarAnimationFinished(false);
    } else {
      timeout = setTimeout(() => {
        setSidebarAnimationFinished(true);
      }, DRIVER_PANEL_SIDEBAR_ANIMATION_IN_MS);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isSidebarCollapsed]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          bgcolor: theme.semantic.surfaceRaised,
          flex: 0,
          pl: isSidebarCollapsed ? 1 : DRIVER_PANEL_TOP_ROW_INNER_GUTTER_SIZE,
          pr: DRIVER_PANEL_TOP_ROW_INNER_GUTTER_SIZE,
          py: '10px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minHeight: `calc(${ORDERS_DISPATCH_HEADER_ROW_HEIGHT_IN_PX}px - ${theme.spacing(DRIVER_PANEL_TOP_ROW_VERTICAL_GUTTER_SIZE * 2)} - ${DRIVER_PANEL_TOP_ROW_BORDER_BOTTOM_WIDTH_IN_PX}px)`,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            {shouldShowContent && (
              <Typography
                color={theme.brandV2.colors.treadBlack}
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {dateString}
              </Typography>
            )}

            <ToggleSidebarButton
              isCollapsed={Boolean(isSidebarCollapsed)}
              onClick={() => ordersDispatchStore.toggleSidebar()}
              sx={{ ml: 'auto' }}
            />
          </Box>

          <Box
            sx={{
              display: shouldShowContent ? 'flex' : 'none',
              flexDirection: 'column',
              gap: 1.5,
              mb: 0.5,
            }}
          >
            <SummaryCounts refreshToken={summaryCountsRefreshToken} />
            <Divider sx={{ mx: DRIVER_PANEL_TOP_ROW_INNER_GUTTER_SIZE * -1 }} />
            <Tabs
              onChange={setSelectedAssigneeType}
              selected={selectedAssigneeType}
              tabs={sortedAssigneeTypeTabs}
              sx={{ mt: -0.5 }}
              tabSx={{ '&.MuiTab-root': { minWidth: 'auto' } }}
            />
            <SearchInput
              onChange={(input) => setSearchQuery((state) => ({ ...state, input }))}
              value={searchQuery.input}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          bgcolor: shouldShowContent
            ? theme.brandV2.colors.treadGray10
            : theme.semantic.surfaceRaised,
          flex: 1,
        }}
      >
        <DriverPanel
          key={selectedAssigneeType}
          ref={ref}
          assigneeType={selectedAssigneeType}
          companyId={companyId}
          onEditDriver={onEditDriver}
          onEditVendor={onEditVendor}
          searchQuery={searchQuery.applied}
          showCards={shouldShowContent}
        />
      </Box>
    </Box>
  );
});

interface DriverPanelProps {
  assigneeType: JobAssignmentType;
  companyId: string;
  onEditDriver?: (driver: DriverTypeahead) => void;
  onEditVendor?: (vendor: AccountCapacityTypeahead) => void;
  searchQuery: string;
  showCards: boolean;
}

type AccountBulkUpdateableFields = { truckCount: number; isAvailable: boolean };

enum AvailabilityFilter {
  ALL = 'all',
  INTERNAL = 'internal',
  EXTERNAL = 'external',
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
}

enum DriverPanelSortBy {
  DRIVER_NAME = 'driver_name',
}

const getAvailabilityFilterOptions = (assigneeType: JobAssignmentType) => {
  const isVendor = assigneeType === JobAssignmentType.VENDOR;
  return [
    {
      label: isVendor ? 'actions.all_vendors' : 'actions.all_drivers',
      value: AvailabilityFilter.ALL,
    },
    ...(!isVendor
      ? [
          { label: 'user.internal_drivers', value: AvailabilityFilter.INTERNAL },
          { label: 'user.external_drivers', value: AvailabilityFilter.EXTERNAL },
        ]
      : []),
    { label: 'actions.available', value: AvailabilityFilter.AVAILABLE },
    { label: 'actions.unavailable', value: AvailabilityFilter.UNAVAILABLE },
  ];
};

const driverPanelSortOptions: AvailableOrderOption<DriverPanelSortBy>[] = [
  {
    label: t('dispatch.dispatch_v2.driver_panel.sort.driver_name'),
    value: DriverPanelSortBy.DRIVER_NAME,
  },
];

const DriverPanel = forwardRef<OrdersViewDriverPanelHandler, DriverPanelProps>(
  function DriverPanel(
    { assigneeType, companyId, onEditDriver, onEditVendor, searchQuery, showCards },
    ref,
  ) {
    const theme = useTheme();
    const { ordersDispatchStore, toasterStore } = useStores();

    const availabilityFilterStorageKey =
      `${OrdersDispatchStorageKeys.DRIVER_PANEL_AVAILABILITY_FILTER}_${assigneeType}` as const;

    const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>(
      () => {
        const stored = localStorage.getItem(availabilityFilterStorageKey);
        const options = getAvailabilityFilterOptions(assigneeType);
        const isValid = options.some((option) => option.value === stored);
        return isValid ? (stored as AvailabilityFilter) : AvailabilityFilter.ALL;
      },
    );
    const availabilityFilterOptions = getAvailabilityFilterOptions(assigneeType);
    const selectedAvailabilityFilterOption = availabilityFilterOptions.find((option) => {
      return option.value === availabilityFilter;
    });

    useEffect(() => {
      localStorage.setItem(availabilityFilterStorageKey, availabilityFilter);
    }, [availabilityFilter, availabilityFilterStorageKey]);

    const [driverPanelSort, setDriverPanelSort] = useState<
      OrderOption<DriverPanelSortBy>
    >({ orderBy: DriverPanelSortBy.DRIVER_NAME, sortBy: 'asc' });

    const handleSortChange = useCallback((order: OrderOption<DriverPanelSortBy>) => {
      setDriverPanelSort(order);
    }, []);

    const driverPanelQuery = useDriverPanelQuery({
      assigneeType,
      availability: availabilityFilter,
      companyId,
      date: ordersDispatchStore.filters.startDate ?? dayjs.tz().format('YYYY-MM-DD'),
      searchQuery,
      shared:
        availabilityFilter === AvailabilityFilter.INTERNAL
          ? false
          : availabilityFilter === AvailabilityFilter.EXTERNAL
            ? true
            : undefined,
      sortName:
        driverPanelSort.orderBy === DriverPanelSortBy.DRIVER_NAME
          ? driverPanelSort.sortBy
          : undefined,
    });
    const vendorRefs = useRef<
      Record<
        string,
        {
          initialValues: AccountBulkUpdateableFields;
          updatedValues: AccountBulkUpdateableFields;
        }
      >
    >({});
    const [isEditVendorsMode, setIsEditVendorsMode] = useState(false);
    const isTypedTruckCapacityEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.typedTruckCapacityV1,
    });
    const isLoading = driverPanelQuery.loading;
    const isShowingVendors = assigneeType === JobAssignmentType.VENDOR;

    const panelItems = driverPanelQuery.data;
    const usesVendorTypeSubCards = isTypedTruckCapacityEnabled && !isEditVendorsMode;
    const usesVendorCardHeights = isShowingVendors && usesVendorTypeSubCards;
    const panelItemSize = useMemo(
      () =>
        usesVendorCardHeights
          ? (index: number) => getVendorPanelItemSize(panelItems[index])
          : DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX,
      [panelItems, usesVendorCardHeights],
    );

    useEffect(() => {
      if (isEditVendorsMode) {
        driverPanelQuery.refetch();
      }
    }, [isEditVendorsMode, ordersDispatchStore.filters.startDate]);

    useEffect(() => {
      if (assigneeType === JobAssignmentType.VENDOR) {
        driverPanelQuery.data.forEach((assignee) => {
          vendorRefs.current[assignee.id] = {
            initialValues: {
              isAvailable: assignee.scheduleOn,
              truckCount: (assignee as AccountCapacityTypeahead).currentCapacityCount,
            },
            updatedValues: {
              isAvailable: assignee.scheduleOn,
              truckCount: (assignee as AccountCapacityTypeahead).currentCapacityCount,
            },
          };
        });
      }
    }, [driverPanelQuery.data, assigneeType]);

    const handleVendorTruckCountChange = useCallback(
      (vendorId: string, truckCount: number) => {
        vendorRefs.current[vendorId] = {
          ...vendorRefs.current[vendorId],
          updatedValues: { ...vendorRefs.current[vendorId].updatedValues, truckCount },
        };
      },
      [],
    );

    const handleVendorAvailabilityChange = useCallback(
      (vendorId: string, isAvailable: boolean) => {
        vendorRefs.current[vendorId] = {
          ...vendorRefs.current[vendorId],
          updatedValues: { ...vendorRefs.current[vendorId].updatedValues, isAvailable },
        };
      },
      [],
    );

    const handleCancelBulkEditVendors = useCallback(() => {
      vendorRefs.current = {};
      setIsEditVendorsMode(false);
    }, []);

    const handleBulkEditVendors = useCallback(async () => {
      const payload = Object.entries(vendorRefs.current).reduce(
        (acc, [vendorId, vendorData]) => {
          const entry: Account_Update_Bulk = { id: vendorId };
          let hasChanges = false;

          if (
            vendorData.initialValues.isAvailable !== vendorData.updatedValues.isAvailable
          ) {
            entry.available = vendorData.updatedValues.isAvailable;
            hasChanges = true;
          }

          if (
            vendorData.initialValues.truckCount !== vendorData.updatedValues.truckCount
          ) {
            entry.connected_account = {
              truck_count: Number(vendorData.updatedValues.truckCount),
            };
            hasChanges = true;
          }

          if (hasChanges) {
            acc.push(entry);
          }

          return acc;
        },
        [] as Array<Account_Update_Bulk>,
      );

      if (payload.length) {
        try {
          await patchV1CompaniesCompanyIdAccountsBulkUpdate({
            path: { 'company-id': companyId },
            body: { data: payload },
          });

          toasterStore.push(
            alert(t('dispatch.dispatch_v2.bulk_updated_vendors'), AlertTypes.success),
          );

          driverPanelQuery.refetch();
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.orders.failed_to_bulk_update_vendors'),
          );
        }
      }

      setIsEditVendorsMode(false);
    }, [companyId]);

    useImperativeHandle(
      ref,
      () => ({
        refetch() {
          driverPanelQuery.refetch();
        },
      }),
      [],
    );

    if (!showCards) {
      return;
    }

    const AvailabilityFilterDropdown = () => {
      return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Menu
            menuTrigger={
              <SmallButton
                color="inherit"
                endIcon={
                  <ArrowDropDown sx={{ color: theme.brandV2.colors.treadGray4 }} />
                }
                size="small"
                variant="outlined"
                sx={{
                  [`&.${buttonClasses.root}`]: {
                    bgcolor: theme.semantic.surfaceRaised,
                    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    flex: 1,
                    justifyContent: 'space-between',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    pl: 1,
                    pr: 0.5,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <FilterList sx={{ fontSize: '16px' }} />

                  <OverflowAwareText sx={{ fontWeight: 600 }}>
                    {t(selectedAvailabilityFilterOption?.label ?? '')}
                  </OverflowAwareText>
                </Box>
              </SmallButton>
            }
          >
            {availabilityFilterOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => setAvailabilityFilter(option.value)}
                data-ui="page-dispatch__orders-view-driver__button"
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.brandV2.colors.treadBlack }}
                  >
                    {t(option.label)}
                  </Typography>

                  {selectedAvailabilityFilterOption?.value === option.value && (
                    <Check
                      sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      );
    };

    return (
      <Box
        sx={{
          borderTop: `solid 1px ${theme.palette.divider}`,
          height: '100%',
          position: 'relative',
          pt: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            ...(isLoading ? { opacity: 0.4, pointerEvents: 'none' } : {}),
          }}
        >
          {isShowingVendors ? (
            <Box sx={{ px: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              {isEditVendorsMode ? (
                <BulkEditVendorsCTAs
                  onBulkEditVendors={handleBulkEditVendors}
                  onCancelBulkEditVendors={handleCancelBulkEditVendors}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <AvailabilityFilterDropdown />

                  <IconButton
                    aria-label={t('actions.edit')}
                    onClick={() => setIsEditVendorsMode(true)}
                    size="small"
                    sx={{
                      [`&.${iconButtonClasses.root}`]: {
                        bgcolor: theme.semantic.surfaceRaised,
                        border: `solid 1px rgba(37, 33, 53, 0.5)`,
                        borderRadius: theme.spacing(0.5),
                        color: theme.brandV2.colors.treadBlack,
                        height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                        ml: 'auto',
                        p: '2px',
                        width: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                      },
                    }}
                    data-ui="page-dispatch__orders-view-driver__button"
                  >
                    <Edit sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ px: 2, py: 0.5 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <AvailabilityFilterDropdown />
                </Box>

                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <OrderBySelector
                    onChange={handleSortChange}
                    order={driverPanelSort}
                    orderOptions={driverPanelSortOptions}
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <InfiniteList
              hasNextPage={driverPanelQuery.hasMore}
              itemCount={panelItems.length}
              estimatedItemSize={DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX}
              itemSize={panelItemSize}
              items={panelItems}
              loadMoreItems={driverPanelQuery.fetchMore}
              style={{ scrollbarWidth: 'none' }}
              renderItem={(index, style) => {
                const assignee = panelItems[index];

                if (!assignee) {
                  throw new Error('Invalid assignee');
                }

                return (
                  <DriverPanelAssigneeCard
                    assignee={assignee}
                    isEditVendorsMode={isEditVendorsMode}
                    onEditDriver={onEditDriver}
                    onEditVendor={onEditVendor}
                    onVendorAvailabilityChange={handleVendorAvailabilityChange}
                    onVendorTruckCountChange={handleVendorTruckCountChange}
                    placement={DriverPanelAssigneeCardPlacement.DRIVER_PANEL}
                    style={style}
                    usesVendorTypeSubCards={usesVendorTypeSubCards}
                  />
                );
              }}
            />
          </Box>
        </Box>
        <LoadingSpinner isVisible={isLoading} sx={{ position: 'absolute', inset: 0 }} />
      </Box>
    );
  },
);

interface BulkEditVendorsCTAsProps {
  onBulkEditVendors: () => Promise<void>;
  onCancelBulkEditVendors: () => void;
}

function BulkEditVendorsCTAs({
  onBulkEditVendors,
  onCancelBulkEditVendors,
}: BulkEditVendorsCTAsProps) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);

  const handleBulkEditVendors = useCallback(async () => {
    setIsEditing(true);
    try {
      await onBulkEditVendors();
    } finally {
      setIsEditing(false);
    }
  }, [onBulkEditVendors]);

  return (
    <>
      <SmallButton
        color="secondary"
        disabled={isEditing}
        onClick={onCancelBulkEditVendors}
        variant="outlined"
        sx={{ flex: 1, bgcolor: theme.semantic.surfaceRaised }}
      >
        {t('actions.cancel')}
      </SmallButton>

      <SmallLoadingButton
        color="brandV2Yellow"
        loading={isEditing}
        onClick={handleBulkEditVendors}
        sx={{ flex: 1 }}
      >
        {t('actions.save')}
      </SmallLoadingButton>
    </>
  );
}

type QueryParamsState = {
  shouldFetch: boolean;
  filters: {
    query: string;
    type: JobAssignmentType;
    availability?: AvailabilityFilter;
    date?: string;
    shared?: boolean;
    sortName?: 'asc' | 'desc';
  };
  pagination?: Pagination & { hasMore: boolean };
};

type UseDriverPanelQuery = {
  assigneeType: JobAssignmentType;
  availability?: AvailabilityFilter;
  companyId: string;
  date?: string;
  searchQuery: string;
  shared?: boolean;
  sortName?: 'asc' | 'desc';
};

function useDriverPanelQuery({
  assigneeType,
  availability,
  companyId,
  date,
  searchQuery,
  shared,
  sortName,
}: UseDriverPanelQuery) {
  const baseQueryKey = 'UseDriverPanelQuery';
  const driversQueryKey = `${baseQueryKey}_drivers`;
  const vendorsQueryKey = `${baseQueryKey}_vendors`;

  const { ordersDispatchStore } = useStores();
  const queryClient = useQueryClient();
  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '', type: assigneeType },
  });

  const driversQuery = useInfiniteQuery({
    queryKey: [driversQueryKey, queryParams.filters, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn:
      queryParams.shouldFetch && queryParams.filters.type === JobAssignmentType.DRIVER
        ? async ({ pageParam, signal }) => {
            try {
              const response = await getV1CompaniesCompanyIdDriversTypeahead({
                path: { 'company-id': companyId },
                query: {
                  'filter[availability]':
                    queryParams.filters.availability === AvailabilityFilter.ALL ||
                    queryParams.filters.availability === AvailabilityFilter.INTERNAL ||
                    queryParams.filters.availability === AvailabilityFilter.EXTERNAL
                      ? undefined
                      : queryParams.filters.availability === AvailabilityFilter.AVAILABLE,
                  'filter[date]': queryParams.filters.date,
                  'filter[dispatchable]': true,
                  'filter[shared]': queryParams.filters.shared,
                  'page[after]': pageParam?.after,
                  'page[limit]': DEFAULT_API_PAGE_SIZE,
                  'search[query]': queryParams.filters.query || undefined,
                  'sort[name]': queryParams.filters.sortName,
                },
                signal,
              });
              const drivers = response.data.data.map((driver) => {
                return DriverTypeahead.parse(driver);
              });
              const pagination = extractPagination(response);

              setQueryParams((state) => ({
                ...state,
                pagination: { ...pagination, hasMore: Boolean(pagination.after) },
              }));

              return drivers;
            } catch (error) {
              if (axios.isCancel(error)) {
                return [];
              }

              connection.handleRequestError(
                error,
                t('error_messages.drivers.failed_to_fetch_drivers'),
              );

              return [];
            }
          }
        : skipToken,
  });

  const vendorsQuery = useInfiniteQuery({
    queryKey: [vendorsQueryKey, queryParams.filters, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn:
      queryParams.shouldFetch && queryParams.filters.type === JobAssignmentType.VENDOR
        ? async ({ pageParam, signal }) => {
            try {
              const response = await getV1CompaniesAccountDayJobStatsTypeahead({
                path: { 'company-id': companyId },
                query: {
                  date: queryParams.filters.date ?? '',
                  'filter[availability]':
                    queryParams.filters.availability === AvailabilityFilter.ALL
                      ? undefined
                      : queryParams.filters.availability === AvailabilityFilter.AVAILABLE,
                  'page[after]': pageParam?.after,
                  'page[limit]': DEFAULT_API_PAGE_SIZE,
                  'search[query]': queryParams.filters.query || undefined,
                },
                signal,
              });
              const accountsCapacity = response.data.data.map((accountCapacity) => {
                return AccountCapacityTypeahead.parse(accountCapacity);
              });
              const pagination = extractPagination(response);

              setQueryParams((state) => ({
                ...state,
                pagination: { ...pagination, hasMore: Boolean(pagination.after) },
              }));

              return accountsCapacity;
            } catch (error) {
              if (axios.isCancel(error)) {
                return [];
              }

              connection.handleRequestError(
                error,
                t('error_messages.account.failed_to_fetch_company_accounts'),
              );

              return [];
            }
          }
        : skipToken,
  });

  const vendorCapacityTypeaheadRTUSubscription = useRef<Subscription | null>(null);
  const companyDayDriverStatRTUSubscription = useRef<Subscription | null>(null);

  const activeQuery = useMemo(() => {
    if (queryParams.filters.type === JobAssignmentType.DRIVER) {
      return driversQuery;
    }

    return vendorsQuery;
  }, [queryParams.filters.type, driversQuery, vendorsQuery]);

  const data = useMemo(() => {
    return activeQuery.data?.pages?.flat() ?? [];
  }, [activeQuery.data?.pages]);

  const fetchMore =
    queryParams.pagination?.hasMore &&
    !activeQuery.isFetchingNextPage &&
    !activeQuery.isFetching
      ? activeQuery.fetchNextPage
      : undefined;

  const clearAllQueriesData = useCallback(() => {
    [driversQueryKey, vendorsQueryKey].forEach((key) => {
      queryClient.setQueriesData({ queryKey: [key] }, () => ({
        pages: [],
        pageParams: [],
      }));
    });
  }, [queryClient, driversQueryKey, vendorsQueryKey]);

  const refetch = useCallback(() => {
    setQueryParams((state) => ({ filters: { ...state.filters }, shouldFetch: false }));
    clearAllQueriesData();
    setTimeout(() => {
      setQueryParams((state) => ({ ...state, shouldFetch: true }));
    }, 100);
  }, [clearAllQueriesData]);

  useEffect(() => {
    ordersDispatchStore.clearDriverPanelAssignees();

    setQueryParams({
      shouldFetch: true,
      filters: {
        query: searchQuery,
        type: assigneeType,
        availability,
        date,
        shared,
        sortName,
      },
    });
  }, [companyId, searchQuery, assigneeType, date, availability, shared, sortName]);

  useEffect(() => {
    ordersDispatchStore.setDriverPanelAssignees(data);
  }, [data]);

  const updateQueryData = (
    query: 'drivers-query' | 'vendors-query',
    item: DriverTypeahead | AccountCapacityTypeahead,
  ) => {
    const queryKey = query === 'drivers-query' ? driversQueryKey : vendorsQueryKey;

    queryClient.setQueriesData(
      { queryKey: [queryKey] },
      (oldData?: { pages: Array<(DriverTypeahead | AccountCapacityTypeahead)[]> }) => {
        if (!oldData) {
          return undefined;
        }

        const newData = {
          ...oldData,
          pages: oldData.pages.map((oldDataPage) => {
            return oldDataPage.map((oldItem) => {
              if (oldItem.id === item.id) {
                return item;
              }

              return oldItem;
            });
          }),
        };

        return newData;
      },
    );
  };

  const subscribeToVendorCapacityTypeaheadChannel = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.VendorCapacityTypeaheadChannel,
      companyId,
      (sub) => {
        vendorCapacityTypeaheadRTUSubscription.current = sub;
      },
      (data: AccountDayJobStat_Read) => {
        const parsedData = AccountCapacityTypeahead.parse(data);

        updateQueryData('vendors-query', parsedData);
      },
      { day: ordersDispatchStore.filters.startDate ?? dayjs.tz().format('YYYY-MM-DD') },
    );
  }, [companyId, ordersDispatchStore.filters.startDate]);

  const subscribeToCompanyDayDriverStatUpdates = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.DriversTypeaheadDayChannel,
      companyId,
      (sub) => {
        companyDayDriverStatRTUSubscription.current = sub;
      },
      (data: Driver_Read_Typeahead) => {
        const parsedData = DriverTypeahead.parse(data);

        updateQueryData('drivers-query', parsedData);
      },
      { day: ordersDispatchStore.filters.startDate ?? dayjs.tz().format('YYYY-MM-DD') },
    );
  }, [companyId, ordersDispatchStore.filters.startDate]);

  useEffect(() => {
    if (queryParams.filters.type === JobAssignmentType.VENDOR) {
      subscribeToVendorCapacityTypeaheadChannel();
    }

    return () => {
      vendorCapacityTypeaheadRTUSubscription.current?.unsubscribe();
    };
  }, [subscribeToVendorCapacityTypeaheadChannel, queryParams.filters.type]);

  useEffect(() => {
    if (queryParams.filters.type === JobAssignmentType.DRIVER) {
      subscribeToCompanyDayDriverStatUpdates();
    }

    return () => {
      companyDayDriverStatRTUSubscription.current?.unsubscribe();
    };
  }, [subscribeToCompanyDayDriverStatUpdates, queryParams.filters.type]);

  return {
    data,
    fetchMore: fetchMore ?? noop,
    hasMore: Boolean(queryParams.pagination?.hasMore),
    loading: activeQuery.isFetchingNextPage || activeQuery.isFetching,
    refetch,
  };
}

export default observer(OrdersViewDriverPanel);
